/**
 * SectionTriggers
 *
 * Sub-component of SectionCard. Owns the UI for adding / removing / configuring
 * the section's `visibleWhen` rules. Triggers compose into a logical OR:
 *   - 0 triggers → visibleWhen = undefined
 *   - 1 trigger  → visibleWhen = SimpleEqualityQuestionVisibility (flat)
 *   - 2+ triggers → visibleWhen = LogicalQuestionVisibility { logicalOperator: 'Or' }
 *
 * A Linear Scale / NPS source instead gates the section on a numeric threshold
 * ("Show section based on answer: NPS score is less than or equal to 6"), writing a
 * ComparisonQuestionVisibility.
 */
import { css } from 'aphrodite';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button } from '../../../../../../../components-v2/Button';
import { Checkbox } from '../../../../../../../components-v2/Checkbox';
import {
	Combobox,
	ComboboxCollection,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxGroup,
	ComboboxGroupLabel,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
} from '../../../../../../../components-v2/Combobox';
import { bs } from '../../../../../../../styles/styles';
import type { NumericComparisonOperator } from '@ViewModels';
import type { QuestionVisibility, SurveyPart } from '../../types';
import { ComparisonRow } from './ComparisonRow';
import { styleSheet } from './SectionTriggers.styles';
import type { Draft, SourceEntry, SourceGroup } from './conditionalVisibilitySources';
import {
	DEFAULT_COMPARISON_OPERATOR,
	buildEligibleSources,
	computeUsedByOtherRows,
	describeSource,
	describeThresholdError,
	fromVisibleWhen,
	groupEligibleSources,
	hydrateDraft,
	isDraftCommittable,
	isNumericSource,
	resolveThresholdBounds,
	toVisibleWhen,
	withOperator,
} from './conditionalVisibilitySources';
import { XIcon } from '../../../../../../../components/svgs/icons/XIcon';

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

export interface SectionTriggersProps {
	/** Zero-based index of this section within allSurveyParts. */
	sectionIndex: number;
	/** react-hook-form field name prefix, e.g. "surveyForm.surveyParts.0". */
	fieldNamePrefix: string;
	/** Full ordered list of survey parts (sections + flat questions). */
	allSurveyParts: SurveyPart[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SectionTriggers: React.FC<SectionTriggersProps> = ({ sectionIndex, fieldNamePrefix, allSurveyParts }) => {
	const { setValue } = useFormContext();

	const visibleWhen = useWatch<Record<string, QuestionVisibility | undefined>>({
		name: `${fieldNamePrefix}.visibleWhen`,
	});

	// Eligible source questions: drop-downs / checkboxes from sections before this one
	const eligibleSources = React.useMemo(
		() => buildEligibleSources(allSurveyParts, sectionIndex),
		[allSurveyParts, sectionIndex]
	);

	// Local edit-time mirror of the rule. Keeps "checkbox enabled but no source
	// yet" representable without writing a half-formed visibleWhen to the form.
	const [draft, setDraft] = React.useState<Draft>(() => hydrateDraft(visibleWhen, eligibleSources));

	// Re-sync draft when external form value changes (duplicate, reload) — but
	// only when our committed shape diverges, so we don't clobber in-progress edits.
	React.useEffect(() => {
		const committed = toVisibleWhen(draft);
		const committedKey = committed ? JSON.stringify(committed) : '';
		const externalKey = visibleWhen ? JSON.stringify(visibleWhen) : '';
		if (committedKey !== externalKey) {
			setDraft(hydrateDraft(visibleWhen, eligibleSources));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visibleWhen]);

	// Same eligible sources, grouped by section for the Combobox's grouped `items`.
	const groupedSources = React.useMemo(
		() => groupEligibleSources(eligibleSources, allSurveyParts),
		[eligibleSources, allSurveyParts]
	);

	const selectedSource = React.useMemo(
		() => eligibleSources.find(s => s.question.fieldId === draft.dependsOnFieldId),
		[eligibleSources, draft.dependsOnFieldId]
	);

	const answerOptions = React.useMemo(() => {
		if (!selectedSource || isNumericSource(selectedSource.question)) return [];
		return (selectedSource.question.options ?? [])
			.filter(o => !!o.label?.trim())
			.map(o => ({ value: o.id ?? o.label ?? '', label: o.label ?? '' }));
	}, [selectedSource]);

	/**
	 * Cap on answer rows: the number of valid options. DropDownQuestion and
	 * CheckBoxesQuestion sources both allow up to N — a rule may reference
	 * every option.
	 *
	 * Deps use the PRIMITIVE `_type` string (not the `selectedSource` object
	 * reference). RHF mutates a question's `_type` in place when a sub-path
	 * setValue lands on it, so the source-question object reference can be
	 * stable across renders even though `_type` changed. Reading the primitive
	 * each render guarantees this memo recomputes when the type flips.
	 */
	const sourceType = selectedSource?.question?._type;
	const maxAnswerRows = React.useMemo(() => {
		if (!sourceType) return 0;
		return answerOptions.length;
	}, [sourceType, answerOptions.length]);

	// -------------------------------------------------------------------------
	// Event handlers
	// -------------------------------------------------------------------------

	/** Commit a draft mutation to both local state and the form's visibleWhen. */
	const commit = React.useCallback(
		(next: Draft) => {
			setDraft(next);
			setValue(`${fieldNamePrefix}.visibleWhen`, toVisibleWhen(next), { shouldDirty: true });
		},
		[fieldNamePrefix, setValue]
	);

	/** Wire the chosen source into the draft (answer values reset). */
	const commitSource = React.useCallback(
		(sourceFieldId: string, isNumeric: boolean) => {
			commit({
				enabled: true,
				dependsOnFieldId: sourceFieldId,
				dependsOnOptionIds: [],
				mode: isNumeric ? 'comparison' : 'options',
				operator: DEFAULT_COMPARISON_OPERATOR,
				threshold: '',
			});
		},
		[commit]
	);

	/** Toggle "Show section based on answer:" checkbox. Unchecking clears the rule. */
	const handleToggle = React.useCallback(
		(checked: boolean | 'indeterminate') => {
			const enabled = Boolean(checked);
			if (!enabled) {
				commit(fromVisibleWhen(undefined));
				return;
			}
			// Enabling without a source — local-only; form stays undefined until a value is picked.
			setDraft(prev => ({ ...prev, enabled: true }));
		},
		[commit]
	);

	/** User picks a source question — wire the visibility rule directly. */
	const handleQuestionChange = React.useCallback(
		(sourceFieldId: string) => {
			const entry = eligibleSources.find(e => e.question.fieldId === sourceFieldId);
			if (!entry) return;
			commitSource(sourceFieldId, isNumericSource(entry.question));
		},
		[eligibleSources, commitSource]
	);

	const handleOperatorChange = React.useCallback(
		(operator: NumericComparisonOperator) => {
			const next = withOperator(draft, selectedSource?.question, operator);
			if (isDraftCommittable(next)) commit(next);
			else setDraft(next);
		},
		[draft, commit, selectedSource]
	);

	const handleThresholdChange = React.useCallback(
		(threshold: string) => {
			const next = { ...draft, threshold };
			if (isDraftCommittable(next)) commit(next);
			else setDraft(next);
		},
		[draft, commit]
	);

	const handleAnswerChange = React.useCallback(
		(answerIndex: number, value: string) => {
			const next = [...draft.dependsOnOptionIds];
			next[answerIndex] = value;
			commit({ ...draft, dependsOnOptionIds: next });
		},
		[draft, commit]
	);

	const handleAnswerAdd = React.useCallback(() => {
		// Add an empty slot — UI shows a fresh select; form only changes once filled.
		setDraft(prev => ({ ...prev, dependsOnOptionIds: [...prev.dependsOnOptionIds, ''] }));
	}, []);

	const handleAnswerRemove = React.useCallback(
		(answerIndex: number) => {
			const next = draft.dependsOnOptionIds.filter((_, i) => i !== answerIndex);
			commit({ ...draft, dependsOnOptionIds: next });
		},
		[draft, commit]
	);

	// -------------------------------------------------------------------------
	// Render
	// -------------------------------------------------------------------------

	if (eligibleSources.length === 0) return null;

	const isReadOnly = !!draft.unsupported;
	const isComparison = draft.mode === 'comparison';
	const thresholdBounds = resolveThresholdBounds(selectedSource?.question, draft.operator);
	const thresholdError = describeThresholdError(draft, thresholdBounds, !!visibleWhen);
	const isQuestionSelectDisabled = !draft.enabled || isReadOnly;
	const isAnswerSelectDisabled = !draft.enabled || !draft.dependsOnFieldId || isReadOnly;
	const answerCount = draft.dependsOnOptionIds.length;
	const hasFirstAnswer = answerCount > 0;
	const additionalAnswerIndices = hasFirstAnswer ? draft.dependsOnOptionIds.slice(1).map((_, i) => i + 1) : [];

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.triggerRow)}>
				<div className={css(styleSheet.triggerStack)}>
					{/* Row 1: the checkbox + label stay on their own line so row 2's
					    dropdowns get the full card width on small monitors. */}
					<div className={css(styleSheet.labelRow)}>
						<Checkbox
							checked={draft.enabled}
							id={`section-trigger-${sectionIndex}-checkbox`}
							onCheckedChange={handleToggle}
						/>
						<span>Show section based on answer:</span>
					</div>
					{/* Row 2: question + "is" + answer(s). Both Comboboxes flex-fill
					    (flex: 1; min-width: 0) so they take as much room as possible
					    and clip with an ellipsis instead of wrapping to a new line. */}
					<div className={css(styleSheet.inputRow)}>
						<div className={css(styleSheet.fill)}>
							{/*
							 * Grouped source-question picker. Item values are the SourceEntry
							 * objects themselves so typeahead filtering + the trigger display run
							 * off the human label (`itemToStringLabel`) rather than the fieldId
							 * UUID. `isItemEqualToValue` matches by fieldId so the controlled
							 * `value` resolves regardless of object identity, and `onValueChange`
							 * funnels the chosen fieldId back through `handleQuestionChange`.
							 */}
							<Combobox
								items={groupedSources}
								value={selectedSource ?? null}
								onValueChange={value => {
									const entry = value as SourceEntry | null;
									if (entry) handleQuestionChange(entry.question.fieldId ?? '');
								}}
								itemToStringLabel={value => describeSource(value as SourceEntry)}
								isItemEqualToValue={(item, value) =>
									(item as SourceEntry)?.question.fieldId === (value as SourceEntry | null)?.question.fieldId
								}
								disabled={isQuestionSelectDisabled}
							>
								<ComboboxInput
									placeholder='Select question...'
									disabled={isQuestionSelectDisabled}
									aria-label='Select source question'
									styles={[styleSheet.sourceComboboxInput]}
								/>
								<ComboboxContent>
									<ComboboxEmpty>No questions found</ComboboxEmpty>
									<ComboboxList>
										{(group: SourceGroup) => (
											<ComboboxGroup key={group.value} items={group.items}>
												<ComboboxGroupLabel>{group.value}</ComboboxGroupLabel>
												<ComboboxCollection>
													{(entry: SourceEntry) => (
														<ComboboxItem key={entry.question.fieldId} value={entry}>
															<ComboboxItemRadioIndicator />
															<span className={css(bs.truncateText, styleSheet.comboboxItemLabel)}>
																{describeSource(entry)}
															</span>
														</ComboboxItem>
													)}
												</ComboboxCollection>
											</ComboboxGroup>
										)}
									</ComboboxList>
								</ComboboxContent>
							</Combobox>
						</div>
						<span className={css(styleSheet.isLabel)}>is</span>
						<div className={css(styleSheet.answerColumn)}>
							{isComparison ? (
								<ComparisonRow
									operator={draft.operator}
									threshold={draft.threshold}
									bounds={thresholdBounds}
									invalidMessage={thresholdError}
									disabled={isAnswerSelectDisabled}
									onOperatorChange={handleOperatorChange}
									onThresholdChange={handleThresholdChange}
								/>
							) : (
								<>
									<AnswerRow
										answerOptions={answerOptions}
										selectedValue={hasFirstAnswer ? draft.dependsOnOptionIds[0] : ''}
										usedByOtherRows={computeUsedByOtherRows(draft.dependsOnOptionIds, 0)}
										answerIndex={0}
										disabled={isAnswerSelectDisabled}
										onAnswerChange={handleAnswerChange}
									/>
									{additionalAnswerIndices.map(answerIdx => (
										<div key={`or-row-${answerIdx}`} className={css(styleSheet.orRow)}>
											<span className={css(styleSheet.orLabel)}>-or-</span>
											<AnswerRow
												answerOptions={answerOptions}
												selectedValue={draft.dependsOnOptionIds[answerIdx] ?? ''}
												usedByOtherRows={computeUsedByOtherRows(draft.dependsOnOptionIds, answerIdx)}
												answerIndex={answerIdx}
												disabled={isAnswerSelectDisabled}
												onAnswerChange={handleAnswerChange}
												showRemove
												onRemove={() => handleAnswerRemove(answerIdx)}
											/>
										</div>
									))}
								</>
							)}
							{/*
							 * "+ Add Answer" hides when:
							 *   - the section is not expanded
							 *   - the rule is disabled or no source picked yet
							 *   - the cap is reached (up to N answer rows, one per option)
							 */}
							{!isComparison && draft.enabled && !!draft.dependsOnFieldId && answerCount < maxAnswerRows && (
								<Button type='button' variant='link' onClick={handleAnswerAdd} styles={[styleSheet.addAnswerButton]}>
									+ Add Answer
								</Button>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

// ---------------------------------------------------------------------------
// AnswerRow sub-component — one OR'd answer value
// ---------------------------------------------------------------------------

interface AnswerRowProps {
	answerOptions: { value: string; label: string }[];
	selectedValue: string;
	/** Options already picked by OTHER rows; rendered disabled in this row's dropdown. */
	usedByOtherRows: Set<string>;
	answerIndex: number;
	disabled: boolean;
	onAnswerChange: (answerIndex: number, value: string) => void;
	showRemove?: boolean;
	onRemove?: () => void;
}

const AnswerRow: React.FC<AnswerRowProps> = React.memo(
	({ answerOptions, selectedValue, usedByOtherRows, answerIndex, disabled, onAnswerChange, showRemove, onRemove }) => {
		const handleChange = React.useCallback(
			(value: string) => onAnswerChange(answerIndex, value),
			[answerIndex, onAnswerChange]
		);

		// Augment options with their per-row disabled state so it's data-visible to
		// both Base UI (item disabling) and the controlled value lookup. `value` is
		// the option object itself so typeahead + the trigger display run off the
		// label (`itemToStringLabel`) rather than the raw option id.
		const items = React.useMemo(
			() => answerOptions.map(o => ({ ...o, disabled: usedByOtherRows.has(o.value) })),
			[answerOptions, usedByOtherRows]
		);

		const selectedItem = React.useMemo(
			() => items.find(o => o.value === selectedValue) ?? null,
			[items, selectedValue]
		);

		return (
			<div className={css(styleSheet.answerRow)}>
				<div className={css(styleSheet.fill)}>
					<Combobox
						items={items}
						value={selectedItem}
						onValueChange={value => {
							const opt = value as { value: string } | null;
							if (opt) handleChange(opt.value);
						}}
						itemToStringLabel={value => (value as { label: string }).label}
						isItemEqualToValue={(item, value) =>
							(item as { value: string })?.value === (value as { value: string } | null)?.value
						}
						disabled={disabled}
					>
						<ComboboxInput
							placeholder='Select value...'
							aria-label='Select trigger value'
							disabled={disabled}
							styles={[styleSheet.sourceComboboxInput]}
						/>
						<ComboboxContent>
							<ComboboxEmpty>No values found</ComboboxEmpty>
							<ComboboxList>
								{(opt: { value: string; label: string; disabled: boolean }) => (
									<ComboboxItem key={opt.value} value={opt} disabled={opt.disabled}>
										<ComboboxItemRadioIndicator />
										<span className={css(bs.truncateText, styleSheet.comboboxItemLabel)}>{opt.label}</span>
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</div>
				{showRemove && (
					<Button
						type='button'
						variant='ghost'
						size='icon'
						onClick={onRemove}
						aria-label={`Remove answer ${answerIndex + 1}`}
						title='Remove this answer'
						styles={[styleSheet.answerRemoveButton]}
					>
						<XIcon width={16} height={16} />
					</Button>
				)}
			</div>
		);
	}
);

AnswerRow.displayName = 'AnswerRow';
